import React, { useEffect } from 'react';

import CButton from '../../../components/CButton';
import CLocalizedText from '../../../components/CLocalizedText';
import CModal from '../../../components/CModal';
import CPageModuleBrowsableTable from '../../../components/CPageModuleBrowsableTable';
import { type TPandasDataFrameDict } from '../../../components/TPandasDataFrameDict';
import { ETableIds } from '../../../enums/ETableIds';
import { type IAugmentedBackendResponse } from '../../../models/IAugmentedBackendResponse';
import { type IResponse } from '../../../models/IResponse';
import { type ICoreTableQuery } from '../../../models/ITradeOrdersTable';
import { SvTradeOrderServices } from '../../../services/SvTradeOrderServices';

export interface TradeOrdersSecurityBrowserState {
  response: IResponse<IAugmentedBackendResponse<TPandasDataFrameDict>>;
  query: ICoreTableQuery;
  onSelect: (isin: string, companyName: string) => void;
}

interface Props {
  securityBrowser?: TradeOrdersSecurityBrowserState;
  setSecurityBrowser: React.Dispatch<React.SetStateAction<TradeOrdersSecurityBrowserState | undefined>>;
}

const DEFAULT_SECURITIES_QUERY: ICoreTableQuery = {
  page: 1,
  sortCol: 1,
  ascending: true,
  pageSize: 8
};

export default function CTradeOrdersSecurityModal(props: Props) {
  const response = props.securityBrowser?.response;
  const query = props.securityBrowser?.query ?? DEFAULT_SECURITIES_QUERY;

  const loadSecurities = async (nextQuery: ICoreTableQuery) => {
    props.setSecurityBrowser((s) =>
      s
        ? {
            ...s,
            query: nextQuery,
            response: {
              ...s.response,
              state: {
                ...s.response.state,
                isLoading: true,
                isError: s.response.state.isError
              }
            }
          }
        : undefined
    );

    const securitiesResponse = await SvTradeOrderServices.getSecurities(nextQuery);
    const securitiesData = await securitiesResponse.getData();
    const isSuccess = await securitiesResponse.isSuccess();

    props.setSecurityBrowser((s) =>
      s
        ? {
            ...s,
            query: nextQuery,
            response: {
              data: securitiesData,
              state: {
                isLoading: false,
                isError: !isSuccess
              }
            }
          }
        : undefined
    );
  };

  useEffect(() => {
    if (props.securityBrowser) {
      void loadSecurities(props.securityBrowser.query);
    }
  }, [!!props.securityBrowser]);

  return (
    <CModal
      onClose={() => {
        props.setSecurityBrowser(undefined);
      }}
      open={!!props.securityBrowser}
    >
      <CPageModuleBrowsableTable
        id={ETableIds.TRADE_ORDERS_SECURITY_BROWSER}
        rowConfig={{}}
        data={response?.data?.results}
        state={response?.state}
        tableMeta={response?.data?.meta}
        style={{ width: '100%' }}
        onUpdateTableMeta={(meta) => {
          void loadSecurities({
            ...query,
            page: meta.page,
            sortCol: meta.sorting.sortCol,
            ascending: meta.sorting.ascending === 'ascending',
            searchText: meta.search
          });
        }}
        columnConfig={[
          {
            // ISIN
            // span: 0
          },
          {
            // Company Name
            header: {
              style: () => {
                return {
                  textAlign: 'left'
                };
              }
            }
          },
          {
            // Security Type
            span: 0
          }
        ]}
        edgeColumnConfig={{
          suffix: {
            header: {
              render: () => ''
            },
            body: {
              render: (context) => {
                const isin = context.rowValues?.[0];
                const companyName = context.rowValues?.[1];

                if (typeof isin !== 'string' || typeof companyName !== 'string') {
                  return null;
                }

                return (
                  <CButton
                    onClick={() => {
                      props.securityBrowser?.onSelect(isin, companyName);
                    }}
                    style={{ borderWidth: 1 }}
                  >
                    <CLocalizedText dictKey={'tradeOrders.securitiesButton'} />
                  </CButton>
                );
              }
            }
          }
        }}
      />
    </CModal>
  );
}
